import React from 'react';
import { Star, Quote } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../context/LanguageContext';

const ReviewsSection = () => {
  const { t, language } = useLanguage();

  const reviews = {
    fr: [
      {
        id: 1,
        name: 'Sophie Martin',
        rating: 5,
        text: 'Service impeccable! La voiture était propre et bien entretenue. Le propriétaire parle très bien anglais et a été très serviable. Je recommande vivement!',
        date: 'Novembre 2024',
      },
      {
        id: 2,
        name: 'Ahmed Ben Salah',
        rating: 5,
        text: 'Excellent rapport qualité-prix. Processus de location très simple et rapide. La Polo était en parfait état. Je reviendrai certainement!',
        date: 'Octobre 2024',
      },
      {
        id: 3,
        name: 'Jean-Pierre Dubois',
        rating: 4,
        text: 'Très bon service client. Les prix sont transparents et les véhicules sont propres. Un peu d\'attente à la réception mais sinon très satisfait.',
        date: 'Septembre 2024',
      },
      {
        id: 4,
        name: 'Maria Santos',
        rating: 5,
        text: 'Meilleure agence de location à La Marsa! Service professionnel, voiture impeccable et personnel très sympathique. Merci UNI CAR!',
        date: 'Août 2024',
      },
    ],
    en: [
      {
        id: 1,
        name: 'Sophie Martin',
        rating: 5,
        text: 'Impeccable service! The car was clean and well maintained. The owner speaks English very well and was very helpful. Highly recommend!',
        date: 'November 2024',
      },
      {
        id: 2,
        name: 'Ahmed Ben Salah',
        rating: 5,
        text: 'Excellent value for money. Very simple and fast rental process. The Polo was in perfect condition. Will definitely come back!',
        date: 'October 2024',
      },
      {
        id: 3,
        name: 'Jean-Pierre Dubois',
        rating: 4,
        text: 'Very good customer service. Prices are transparent and vehicles are clean. A bit of waiting at pickup but otherwise very satisfied.',
        date: 'September 2024',
      },
      {
        id: 4,
        name: 'Maria Santos',
        rating: 5,
        text: 'Best rental agency in La Marsa! Professional service, impeccable car and very friendly staff. Thank you UNI CAR!',
        date: 'August 2024',
      },
    ],
    de: [
      {
        id: 1,
        name: 'Sophie Martin',
        rating: 5,
        text: 'Tadelloser Service! Das Auto war sauber und gut gewartet. Der Besitzer spricht sehr gut Englisch und war sehr hilfsbereit. Sehr empfehlenswert!',
        date: 'November 2024',
      },
      {
        id: 2,
        name: 'Ahmed Ben Salah',
        rating: 5,
        text: 'Ausgezeichnetes Preis-Leistungs-Verhältnis. Sehr einfacher und schneller Mietprozess. Der Polo war in perfektem Zustand. Komme auf jeden Fall wieder!',
        date: 'Oktober 2024',
      },
      {
        id: 3,
        name: 'Jean-Pierre Dubois',
        rating: 4,
        text: 'Sehr guter Kundenservice. Preise sind transparent und Fahrzeuge sind sauber. Etwas Wartezeit bei der Abholung, aber sonst sehr zufrieden.',
        date: 'September 2024',
      },
      {
        id: 4,
        name: 'Maria Santos',
        rating: 5,
        text: 'Beste Autovermietung in La Marsa! Professioneller Service, einwandfreies Auto und sehr freundliches Personal. Danke UNI CAR!',
        date: 'August 2024',
      },
    ],
  };

  const currentReviews = reviews[language] || reviews.fr;

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="py-16 sm:py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-[#00bfa5]/10 text-[#00bfa5] rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
            4.5/5 {t('reviews.rating')}
          </span>
          <h2
            data-testid="reviews-title"
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#606060] mb-3 sm:mb-4 font-['Outfit'] tracking-tight"
          >
            {t('reviews.title')}
          </h2>
          <p
            data-testid="reviews-subtitle"
            className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4"
          >
            {t('reviews.subtitle')}
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {currentReviews.map((review) => (
            <Card
              key={review.id}
              data-testid={`review-card-${review.id}`}
              className="bg-accent/30 border-0 rounded-xl sm:rounded-2xl hover:shadow-lg transition-shadow"
            >
              <CardContent className="p-4 sm:p-6">
                {/* Quote Icon */}
                <div className="mb-3 sm:mb-4">
                  <Quote className="w-6 h-6 sm:w-8 sm:h-8 text-[#00bfa5]/30" />
                </div>

                {/* Rating Stars */}
                <div className="flex items-center gap-0.5 sm:gap-1 mb-3 sm:mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-3 h-3 sm:w-4 sm:h-4 ${
                        i < review.rating
                          ? 'text-[#00bfa5] fill-[#00bfa5]'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-foreground mb-4 sm:mb-6 leading-relaxed text-xs sm:text-sm line-clamp-4">
                  "{review.text}"
                </p>

                {/* Reviewer Info */}
                <div className="pt-3 sm:pt-4 border-t border-border/50">
                  <p className="font-semibold text-[#606060] font-['Outfit'] text-sm sm:text-base">
                    {review.name}
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground">{review.date}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Google Reviews Link */}
        <div className="text-center mt-12">
          <a
            href="https://www.google.com/maps/place/UNI+CAR"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-link"
            className="inline-flex items-center gap-2 text-[#606060] hover:text-[#00bfa5] transition-colors font-medium"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Voir tous les avis sur Google
          </a>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
